import { useEffect, useMemo, useState } from "react";
import {
  COLORS,
  HoursBar,
  PunchTimes,
  StatusBadge,
  WeekNavigator,
} from "./attendanceUi";
import {
  MONTH_NAMES,
  buildYearOptions,
  defaultWeekIndexForMonth,
  getWeeksInMonth,
} from "./attendanceMonthWeek";
import {
  getAttendanceDisplayMode,
  getDayTemporalState,
  isDayPillDisabled,
  todayIso,
} from "./attendanceDayGate";
import { fetchEmployeeProfileDetails } from "./employeeAttendanceApi";
import { useEmployeeAttendanceData } from "./useEmployeeAttendanceData";
import { useHideAttendanceLayoutChrome } from "./useHideAttendanceLayoutChrome";
import { fetchWfHolidays } from "../../../../Moduels/attendance/wfApi";
import {
  buildHolidayDateMap,
  calendarDayBaseClass,
  parseWorkDate,
  weekendPillClass,
} from "../../../../Moduels/attendance/attendanceCalendarUtils";
import { HrHero, HrPanel } from "../../../hr/HrDesignSystem";
import BrixigoPageLoader from "../../../common/BrixigoPageLoader";
import { User } from "lucide-react";

const now = new Date();

function hasApiRecord(rec) {
  return rec && !rec.isEmpty;
}

function getProfileFromAuth(profile, userId, extras) {
  const emp = profile?.employee;
  const legacyEmp = profile?.legacy ? profile?.employee : null;

  const name = emp
    ? [emp.first_name, emp.last_name].filter(Boolean).join(" ")
    : legacyEmp?.name || "—";

  const email = emp?.email || legacyEmp?.email || "—";
  const employeeId = userId || emp?.employee_id || legacyEmp?.employee_id || "—";

  return {
    employeeId,
    name: name || "—",
    email,
    department: extras?.department ?? "—",
    designation: extras?.designation ?? "—",
    shift: extras?.shift ?? "—",
  };
}

function countStatus(records, status) {
  return records.filter((r) => hasApiRecord(r) && r.status === status).length;
}

function sumRealHours(records) {
  return records
    .filter(hasApiRecord)
    .reduce((sum, r) => sum + (Number(r.hoursWorked) || 0), 0);
}

function isNotMarkedYet(rec, dateStr) {
  if (getDayTemporalState(dateStr) !== "today") return false;
  if (!hasApiRecord(rec)) return true;
  const { mode } = getAttendanceDisplayMode(dateStr, rec);
  if (mode === "not_started") return true;
  return !rec.checkIn && !rec.checkOut;
}

function DayCard({ day, rec, isActive, onSelect, shiftLabel, orgHoliday }) {
  const temporal = getDayTemporalState(day.date);
  const { mode } = getAttendanceDisplayMode(day.date, rec);
  const isFuture = temporal === "future";
  const disabled = isDayPillDisabled(day.date);
  const apiRec = hasApiRecord(rec);
  const weekdayName = parseWorkDate(day.date)?.weekdayName || "";
  const hasStatus = apiRec && rec?.status;
  const toneClass = calendarDayBaseClass(weekdayName, orgHoliday, !!hasStatus);

  const border = isActive
    ? "border-2 border-teal-500 ring-1 ring-teal-100"
    : isFuture
      ? `border opacity-80 ${orgHoliday ? "border-violet-300" : "border-slate-100 bg-slate-50"}`
      : orgHoliday
        ? "border border-violet-400 ring-1 ring-violet-200"
        : hasStatus
          ? "border border-slate-200 hover:border-slate-300"
          : `border ${toneClass} hover:border-slate-300`;

  return (
    <button
      type="button"
      disabled={disabled}
      onClick={() => !disabled && onSelect(day.date)}
      className={`rounded-xl p-3 text-left w-full transition ${border} ${
        disabled ? "cursor-not-allowed" : "cursor-pointer"
      }`}
    >
      <p className="text-xs font-medium text-slate-700">{day.label}</p>
      <p className="text-[10px] text-slate-400 mb-2">
        {new Date(`${day.date}T12:00:00`).toLocaleDateString("en-IN", {
          day: "numeric",
          month: "short",
        })}
      </p>
      {orgHoliday && !hasStatus && (
        <>
          <StatusBadge status="holiday" className="mb-2" />
          <p className="text-[10px] font-medium text-violet-900 truncate" title={orgHoliday.name}>
            {orgHoliday.name}
          </p>
        </>
      )}
      {mode === "future_empty" && !orgHoliday && <p className="text-sm text-slate-400">—</p>}
      {mode === "future_holiday" && !orgHoliday && <StatusBadge status="holiday" className="mb-2" />}
      {mode === "not_started" && (
        <p className="text-[10px] text-slate-400">Day not started</p>
      )}
      {mode === "full" && !apiRec && <p className="text-sm text-slate-400">—</p>}
      {mode === "full" && apiRec && (
        <>
          <StatusBadge status={rec.status} className="mb-2" />
          <PunchTimes checkIn={rec.checkIn} checkOut={rec.checkOut} compact />
          <p className="text-xs text-slate-600 mt-2 tabular-nums">
            {rec.hoursWorked ? `${rec.hoursWorked}h` : "0h"}
          </p>
        </>
      )}
      {shiftLabel && shiftLabel !== "—" && (
        <p className="text-[10px] text-slate-400 mt-1 truncate" title={shiftLabel}>
          {shiftLabel}
        </p>
      )}
    </button>
  );
}

export default function EmployeeAttendanceView({ userId, profile }) {
  useHideAttendanceLayoutChrome();

  const [month, setMonth] = useState(now.getMonth());
  const [year, setYear] = useState(now.getFullYear());
  const [selectedDate, setSelectedDate] = useState(null);
  const [profileExtras, setProfileExtras] = useState({
    department: "—",
    designation: "—",
    shift: "—",
  });
  const [profileLoading, setProfileLoading] = useState(true);

  const weeksInMonth = useMemo(() => getWeeksInMonth(year, month), [year, month]);

  const [weekIndex, setWeekIndex] = useState(() =>
    defaultWeekIndexForMonth(
      now.getFullYear(),
      now.getMonth(),
      getWeeksInMonth(now.getFullYear(), now.getMonth())
    )
  );

  const weekStart = weeksInMonth[weekIndex] ?? weeksInMonth[0] ?? null;

  const { records, weekDays, weekLabel, loading, error, apiRowCount } =
    useEmployeeAttendanceData(userId, weekStart);

  const [holidays, setHolidays] = useState([]);
  const holidayByDate = useMemo(() => buildHolidayDateMap(holidays), [holidays]);

  useEffect(() => {
    if (!weekDays.length) {
      setHolidays([]);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const rows = await fetchWfHolidays({
          from_date: weekDays[0].date,
          to_date: weekDays[weekDays.length - 1].date,
        });
        if (!cancelled) setHolidays(Array.isArray(rows) ? rows : []);
      } catch {
        if (!cancelled) setHolidays([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [weekDays]);

  useEffect(() => {
    if (!userId) {
      setProfileLoading(false);
      return;
    }
    let cancelled = false;
    setProfileLoading(true);
    fetchEmployeeProfileDetails(userId)
      .then((extras) => {
        if (!cancelled) setProfileExtras(extras);
      })
      .finally(() => {
        if (!cancelled) setProfileLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [userId]);

  const empProfile = useMemo(
    () => getProfileFromAuth(profile, userId, profileExtras),
    [profile, userId, profileExtras]
  );

  const yearOptions = useMemo(() => buildYearOptions(now.getFullYear()), []);

  const handleMonthChange = (nextMonth) => {
    setMonth(Number(nextMonth));
    setWeekIndex(0);
    setSelectedDate(null);
  };

  const handleYearChange = (nextYear) => {
    setYear(Number(nextYear));
    setWeekIndex(0);
    setSelectedDate(null);
  };

  const handleDayPillClick = (date) => {
    if (isDayPillDisabled(date)) return;
    setSelectedDate((prev) => (prev === date ? null : date));
  };

  const prevDisabled = weekIndex <= 0;
  const nextDisabled = weekIndex >= weeksInMonth.length - 1;

  const todayDate = todayIso();
  const focusDate = selectedDate ?? todayDate;
  const focusDay = weekDays.find((d) => d.date === focusDate);
  const focusRecord = records.find((r) => r.date === focusDate) ?? null;
  const focusMode = getAttendanceDisplayMode(focusDate, focusRecord).mode;
  const focusNotMarked = isNotMarkedYet(focusRecord, focusDate);

  const cardTitle =
    selectedDate === null
      ? "Today"
      : `${(focusDay?.label || "").toUpperCase()} — ${focusDate}`;

  const weeklyStats = useMemo(
    () => ({
      present: countStatus(records, "present"),
      absent: countStatus(records, "absent"),
      leave: countStatus(records, "leave"),
      halfDay: countStatus(records, "half-day"),
      totalHours: sumRealHours(records),
    }),
    [records]
  );

  const focusBorder =
    hasApiRecord(focusRecord) && focusRecord.status === "present"
      ? "border-emerald-400"
      : hasApiRecord(focusRecord) && focusRecord.status === "absent"
        ? "border-red-400"
        : hasApiRecord(focusRecord) && focusRecord.status === "leave"
          ? "border-amber-400"
          : hasApiRecord(focusRecord) && focusRecord.status === "half-day"
            ? "border-sky-400"
            : hasApiRecord(focusRecord) && focusRecord.status === "holiday"
              ? "border-violet-400"
              : focusDay?.isToday
                ? "border-teal-400"
                : "border-slate-200";

  const summaryItems = [
    { label: "Present", value: weeklyStats.present },
    { label: "Absent", value: weeklyStats.absent },
    { label: "On Leave", value: weeklyStats.leave },
    { label: "Half Day", value: weeklyStats.halfDay },
    { label: "Total Hours", value: `${weeklyStats.totalHours.toFixed(1)}h` },
  ];

  const weekHasNoApiRows = !loading && apiRowCount === 0;

  const monthYearSelector = (
    <div className="flex items-center gap-2">
      <select
        value={month}
        onChange={(e) => handleMonthChange(e.target.value)}
        className="border border-slate-200 rounded-lg px-3 py-2 text-sm bg-white text-slate-800"
        aria-label="Select month"
      >
        {MONTH_NAMES.map((name, idx) => (
          <option key={name} value={idx}>
            {name}
          </option>
        ))}
      </select>
      <select
        value={year}
        onChange={(e) => handleYearChange(e.target.value)}
        className="border border-slate-200 rounded-lg px-3 py-2 text-sm bg-white text-slate-800"
        aria-label="Select year"
      >
        {yearOptions.map((y) => (
          <option key={y} value={y}>
            {y}
          </option>
        ))}
      </select>
    </div>
  );

  function renderCheckIn() {
    if (focusMode === "not_started" || focusNotMarked) {
      return <span className="text-lg font-medium text-slate-500">Not marked yet</span>;
    }
    if (!hasApiRecord(focusRecord)) {
      return <span className="text-slate-400">—</span>;
    }
    return focusRecord.checkIn || "—";
  }

  function renderCheckOut() {
    if (focusMode === "not_started" || focusNotMarked) {
      return <span className="text-lg font-medium text-slate-500">Not marked yet</span>;
    }
    if (!hasApiRecord(focusRecord)) {
      return <span className="text-slate-400">—</span>;
    }
    return focusRecord.checkOut || "—";
  }

  function renderHours() {
    if (focusMode === "not_started" || focusNotMarked) return "0h";
    if (!hasApiRecord(focusRecord)) return "0h";
    return `${focusRecord.hoursWorked ?? 0}h`;
  }

  const weekStats = [
    { label: "Present", value: countStatus(records, "present") },
    { label: "Absent", value: countStatus(records, "absent") },
    { label: "Leave", value: countStatus(records, "leave") },
    { label: "Hours", value: `${sumRealHours(records).toFixed(1)}h` },
  ];

  return (
    <div className="ind-cal-page p-4 md:p-6 w-full max-w-full min-w-0 space-y-6">
      <HrHero
        eyebrow={
          <span className="inline-flex items-center gap-2">
            <User className="w-4 h-4" />
            My Attendance
          </span>
        }
        title={empProfile.name}
        subtitle={`${empProfile.designation} · ${MONTH_NAMES[month]} ${year}`}
        stats={weekStats}
        actions={
          <WeekNavigator
            weekLabel={weekLabel}
            onPrev={() => {
              setWeekIndex((i) => Math.max(0, i - 1));
              setSelectedDate(null);
            }}
            onNext={() => {
              setWeekIndex((i) => Math.min(weeksInMonth.length - 1, i + 1));
              setSelectedDate(null);
            }}
            prevDisabled={prevDisabled}
            nextDisabled={nextDisabled}
            leading={monthYearSelector}
          />
        }
      />

      {loading && (
        <BrixigoPageLoader compact message="Loading…" fullPage={false} />
      )}

      {error && (
        <div className="p-3 text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-lg">
          {error}
        </div>
      )}

      <div className="flex flex-wrap gap-2">
        {weekDays.map((day) => {
          const disabled = isDayPillDisabled(day.date);
          const isActive =
            selectedDate === day.date || (selectedDate === null && day.isToday);
          const orgHoliday = holidayByDate[day.date];
          return (
            <button
              key={day.date}
              type="button"
              disabled={disabled}
              onClick={() => handleDayPillClick(day.date)}
              className={`px-4 py-2 rounded-full text-sm font-medium border transition ${
                disabled
                  ? "bg-slate-50 border-slate-100 text-slate-400 cursor-not-allowed"
                  : orgHoliday
                    ? isActive
                      ? "bg-violet-700 text-white border-violet-700"
                      : "bg-violet-100 border-violet-300 text-violet-900 hover:border-violet-400"
                    : day.isToday
                      ? isActive
                        ? "bg-slate-800 text-white border-slate-800"
                        : "bg-white border-teal-400 text-teal-800"
                      : weekendPillClass(day.label, isActive, false)
              }`}
            >
              {day.label}
              {day.isToday && !disabled && (
                <span className="ml-1 text-[10px] opacity-80">· today</span>
              )}
            </button>
          );
        })}
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        <HrPanel title="Profile" subtitle="Employee details">
          {profileLoading ? (
            <BrixigoPageLoader compact message="Loading profile…" fullPage={false} />
          ) : (
            <>
              <h2 className="text-lg font-semibold text-slate-900">{empProfile.name}</h2>
              <p className="text-sm text-slate-600 mt-1">{empProfile.email}</p>
              <p className="text-sm text-slate-500 mt-2">
                Department: {empProfile.department}
              </p>
              <p className="text-sm text-slate-500">
                Designation: {empProfile.designation}
              </p>
              <p className="text-xs text-slate-400 mt-2">ID: {empProfile.employeeId}</p>
              <span className="inline-block mt-3 px-3 py-1 rounded-full text-xs font-medium border border-slate-200 text-slate-600 bg-slate-50">
                Shift: {empProfile.shift}
              </span>
              {(empProfile.department === "—" ||
                empProfile.designation === "—" ||
                empProfile.shift === "—") && (
                <p className="text-xs text-slate-400 mt-3">
                  Department and shift info will appear once profile is linked
                </p>
              )}
            </>
          )}
        </HrPanel>

        <HrPanel title={cardTitle} subtitle="Selected day punches" className={focusBorder}>
          {loading ? (
            <BrixigoPageLoader compact fullPage={false} />
          ) : (
            <>
              <div className="flex justify-between items-start gap-4">
                <div>
                  <p className="text-xs text-slate-500">Check In</p>
                  <p className="text-3xl font-semibold text-slate-900 tabular-nums">
                    {renderCheckIn()}
                  </p>
                </div>
                <div className="text-right">
                  <p className="text-xs text-slate-500">Check Out</p>
                  <p className="text-3xl font-semibold text-slate-900 tabular-nums">
                    {renderCheckOut()}
                  </p>
                </div>
              </div>
              <div className="mt-4 flex items-center justify-between">
                <div>
                  <p className="text-xs text-slate-500">Hours worked</p>
                  <p className="text-xl font-semibold text-slate-800">{renderHours()}</p>
                </div>
                {hasApiRecord(focusRecord) &&
                  focusMode === "full" &&
                  !focusNotMarked && (
                    <StatusBadge status={focusRecord.status} />
                  )}
                {focusMode === "not_started" && (
                  <span className="text-xs text-slate-400">Day not started</span>
                )}
              </div>
            </>
          )}
        </HrPanel>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
        {summaryItems.map((s) => (
          <div
            key={s.label}
            className="hr-stat-card text-center py-3"
          >
            <p className="text-[10px] uppercase text-slate-500">{s.label}</p>
            <p className="text-lg font-semibold text-slate-900 mt-1">
              {loading ? 0 : s.value}
            </p>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-2">
        {weekDays.map((day) => {
          const rec = records.find((r) => r.date === day.date);
          const isActive =
            selectedDate === day.date ||
            (selectedDate === null && day.isToday && !isDayPillDisabled(day.date));
          return (
            <DayCard
              key={day.date}
              day={day}
              rec={rec}
              isActive={isActive}
              onSelect={handleDayPillClick}
              shiftLabel={empProfile.shift}
              orgHoliday={holidayByDate[day.date]}
            />
          );
        })}
      </div>

      <HrPanel title="Detailed log" subtitle="Week-wise attendance entries" noPadding>
        {loading ? (
          <BrixigoPageLoader compact fullPage={false} />
        ) : weekHasNoApiRows ? (
          <p className="p-6 text-center text-slate-500 text-sm">
            No attendance records found for this week
          </p>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-slate-50 text-left text-slate-600">
                <th className="p-3">Day</th>
                <th className="p-3">Date</th>
                <th className="p-3">Status</th>
                <th className="p-3">Check In</th>
                <th className="p-3">Check Out</th>
                <th className="p-3">Hours</th>
                <th className="p-3">Shift</th>
              </tr>
            </thead>
            <tbody>
              {weekDays.map((day) => {
                const rec = records.find((r) => r.date === day.date);
                const { mode } = getAttendanceDisplayMode(day.date, rec);
                const isFuture = getDayTemporalState(day.date) === "future";
                const apiRec = hasApiRecord(rec);
                const orgHoliday = holidayByDate[day.date];
                const rowTone = orgHoliday
                  ? "bg-violet-50/60"
                  : day.label === "Sat"
                    ? "bg-indigo-50/40"
                    : day.label === "Sun"
                      ? "bg-rose-50/40"
                      : "";

                return (
                  <tr
                    key={day.date}
                    className={`border-t border-slate-100 ${rowTone} ${
                      day.date === focusDate ? "bg-teal-50/40" : ""
                    } ${isFuture && !orgHoliday ? "bg-slate-50/50" : ""}`}
                  >
                    <td className="p-3 font-medium">{day.label}</td>
                    <td className="p-3 text-slate-600 tabular-nums">{day.date}</td>
                    <td className="p-3">
                      {orgHoliday && !apiRec && (
                        <div className="flex flex-col gap-1">
                          <StatusBadge status="holiday" />
                          <span className="text-xs text-violet-800">{orgHoliday.name}</span>
                        </div>
                      )}
                      {mode === "future_empty" && !orgHoliday && (
                        <span className="text-slate-400">—</span>
                      )}
                      {mode === "future_holiday" && !orgHoliday && (
                        <StatusBadge status="holiday" />
                      )}
                      {mode === "not_started" && (
                        <span className="text-slate-400 text-xs">Day not started</span>
                      )}
                      {mode === "full" && !apiRec && <span className="text-slate-400">—</span>}
                      {mode === "full" && apiRec && <StatusBadge status={rec.status} />}
                    </td>
                    <td className="p-3 tabular-nums">
                      {mode === "full" && !isFuture && apiRec
                        ? rec.checkIn || "—"
                        : mode === "full" && !isFuture && isNotMarkedYet(rec, day.date)
                          ? "Not marked yet"
                          : "—"}
                    </td>
                    <td className="p-3 tabular-nums">
                      {mode === "full" && !isFuture && apiRec
                        ? rec.checkOut || "—"
                        : mode === "full" && !isFuture && isNotMarkedYet(rec, day.date)
                          ? "Not marked yet"
                          : "—"}
                    </td>
                    <td className="p-3">
                      {mode === "full" && !isFuture && apiRec ? (
                        <HoursBar hours={rec.hoursWorked || 0} />
                      ) : (
                        <span className="text-slate-400">—</span>
                      )}
                    </td>
                    <td className="p-3 text-slate-600 text-xs">{empProfile.shift}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </HrPanel>
    </div>
  );
}
